import type { FredrickLocalSearchHistoryEntryV1 } from 'fredrick-local-repository';

export interface PriceHistoryPoint {
  readonly searchId: string;
  readonly x: number;
  readonly yMin: number;
  readonly yP25: number;
  readonly yMedian: number;
  readonly yP75: number;
  readonly yMax: number;
}

export interface PriceHistoryGeometry {
  readonly width: number;
  readonly height: number;
  readonly plotLeft: number;
  readonly plotRight: number;
  readonly plotTop: number;
  readonly plotBottom: number;
  readonly valueMin: number;
  readonly valueMax: number;
  readonly points: readonly PriceHistoryPoint[];
}

function compareEntries(left: FredrickLocalSearchHistoryEntryV1, right: FredrickLocalSearchHistoryEntryV1): number {
  return left.summary.observedAt - right.summary.observedAt
    || left.summary.observedAtEnd - right.summary.observedAtEnd
    || (left.summary.searchId < right.summary.searchId ? -1 : left.summary.searchId > right.summary.searchId ? 1 : 0);
}

export function calculatePriceHistoryGeometry(
  entries: readonly FredrickLocalSearchHistoryEntryV1[],
  width = 760,
  height = 260,
): PriceHistoryGeometry {
  const plotLeft = 58;
  const plotRight = Math.max(plotLeft + 1, width - 18);
  const plotTop = 16;
  const plotBottom = Math.max(plotTop + 1, height - 38);
  const priced = [...entries].filter((entry) => entry.summary.prices !== null).sort(compareEntries);
  const rawMin = priced.length === 0 ? 0 : Math.min(...priced.map((entry) => entry.summary.prices!.min));
  const rawMax = priced.length === 0 ? 1 : Math.max(...priced.map((entry) => entry.summary.prices!.max));
  const padding = rawMin === rawMax ? Math.max(1, Math.abs(rawMin) * 0.05) : 0;
  const valueMin = rawMin - padding;
  const valueMax = rawMax + padding;
  const valueSpan = Math.max(1, valueMax - valueMin);
  const minTime = priced.length === 0 ? 0 : priced[0]!.summary.observedAt;
  const maxTime = priced.length === 0 ? 1 : priced.at(-1)!.summary.observedAt;
  const tiedTimes = minTime === maxTime;

  const y = (value: number) => plotBottom - ((value - valueMin) / valueSpan) * (plotBottom - plotTop);
  const points = priced.map((entry, index): PriceHistoryPoint => {
    const x = priced.length === 1
      ? (plotLeft + plotRight) / 2
      : tiedTimes
        ? plotLeft + (index / (priced.length - 1)) * (plotRight - plotLeft)
        : plotLeft + ((entry.summary.observedAt - minTime) / (maxTime - minTime)) * (plotRight - plotLeft);
    const prices = entry.summary.prices!;
    return {
      searchId: entry.summary.searchId,
      x,
      yMin: y(prices.min),
      yP25: y(prices.p25),
      yMedian: y(prices.median),
      yP75: y(prices.p75),
      yMax: y(prices.max),
    };
  });

  return { width, height, plotLeft, plotRight, plotTop, plotBottom, valueMin, valueMax, points };
}
